/**
 * Shared report filter presets — mirrors backend ReportFilterService.
 */

export const REPORT_PERIOD_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'year', label: 'Year to date' },
  { id: 'custom', label: 'Custom range' },
]

export const REPORT_COMPARE_OPTIONS = [
  { id: 'none', label: 'No comparison' },
  { id: 'previous_period', label: 'Previous period' },
  { id: 'previous_year', label: 'Previous year' },
]

/**
 * @param {'today' | 'week' | 'month' | 'last_month' | 'year' | 'custom'} period
 * @param {{ from?: string, to?: string }} [custom]
 * @returns {{ from: string, to: string }}
 */
export function resolveReportPeriodBounds(period, custom = {}) {
  if (period === 'custom') {
    if (!custom.from || !custom.to) {
      throw new Error('Custom period requires from and to dates.')
    }
    return { from: custom.from, to: custom.to }
  }

  const today = new Date()
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  if (period === 'today') {
    const iso = toIsoDate(end)
    return { from: iso, to: iso }
  }

  if (period === 'week') {
    const start = new Date(end)
    const day = start.getDay()
    const diff = day === 0 ? 6 : day - 1
    start.setDate(start.getDate() - diff)
    return { from: toIsoDate(start), to: toIsoDate(end) }
  }

  if (period === 'last_month') {
    const start = new Date(end.getFullYear(), end.getMonth() - 1, 1)
    const lastDay = new Date(end.getFullYear(), end.getMonth(), 0)
    return { from: toIsoDate(start), to: toIsoDate(lastDay) }
  }

  if (period === 'year') {
    const start = new Date(end.getFullYear(), 0, 1)
    return { from: toIsoDate(start), to: toIsoDate(end) }
  }

  const start = new Date(end.getFullYear(), end.getMonth(), 1)
  return { from: toIsoDate(start), to: toIsoDate(end) }
}

/**
 * @param {string} from
 * @param {string} to
 * @param {'none' | 'previous_period' | 'previous_year'} compareMode
 * @returns {{ from: string, to: string } | null}
 */
export function resolveComparePeriodBounds(from, to, compareMode) {
  if (compareMode === 'none') return null

  const start = parseIsoDate(from)
  const end = parseIsoDate(to)
  const days = diffDays(start, end) + 1

  if (compareMode === 'previous_year') {
    const compareStart = new Date(start)
    compareStart.setFullYear(compareStart.getFullYear() - 1)
    const compareEnd = new Date(end)
    compareEnd.setFullYear(compareEnd.getFullYear() - 1)
    return { from: toIsoDate(compareStart), to: toIsoDate(compareEnd) }
  }

  if (compareMode === 'previous_period') {
    const compareEnd = new Date(start)
    compareEnd.setDate(compareEnd.getDate() - 1)
    const compareStart = new Date(compareEnd)
    compareStart.setDate(compareStart.getDate() - (days - 1))
    return { from: toIsoDate(compareStart), to: toIsoDate(compareEnd) }
  }

  return null
}

/**
 * @param {Date} start
 * @param {Date} end
 */
function diffDays(start, end) {
  const ms = end.getTime() - start.getTime()
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

/**
 * @param {string} isoDate
 */
function parseIsoDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * @param {Date} date
 */
function toIsoDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
